// file: client/src/offline/offlineGate.js
/**
 * MODULE 09 — the offline-first interception seam (axios adapter, the
 * beta-mock precedent). It is a CIRCUIT BREAKER, not a mode switch:
 *
 *   · Module off / terminal not enrolled → pure pass-through (byte-identical).
 *   · Status OFFLINE → serve from the local database immediately.
 *   · Status ONLINE → try the network; on a *connectivity* failure (no HTTP
 *     response — server unreachable, DNS, timeout) flip to OFFLINE INSTANTLY
 *     and re-route the same request through the local handler. This is the
 *     fix for the "app freezes for 15 s on a dropped connection" problem:
 *     the user feels at most one slow request, then everything is local.
 *   · Real HTTP errors (4xx/5xx with a response) propagate unchanged.
 *   · Successful online GETs of reference data warm the local cache so the
 *     next outage degrades with fresh data ("maintain synchronization").
 *
 * Doctrine (plan §8.2): an action is FULLY functional offline, QUEUED with
 * explicit copy, or DISABLED with the reason — never a silent third state.
 * Reads that the cache can't answer return an EMPTY success (the page shows
 * its empty state) rather than a 503 that crashes it.
 */
import { useOfflineStore } from "./offlineStore.js";
import { ulid } from "./ulid.js";
import { enqueue } from "./outbox.js";
import {
  searchProduits,
  listProduits,
  getProduit,
  listLots,
  searchClients,
  getClient,
  listMedecins,
  applyLocalDeduction,
  resoudreCode,
} from "./localStore.js";
import { idbPut, idbGet, idbDelete, metaGet, metaSet } from "./idb.js";

// ── axios-shaped helpers ──────────────────────────────────────────────────────
const ok = (config, body, status = 200) => ({
  data: body,
  status,
  statusText: "OK",
  headers: {},
  config,
  request: { offline: true },
});

const offlineError = (config, message) => {
  const err = new Error(message);
  err.config = config;
  err.response = {
    status: 503,
    data: { success: false, code: "OFFLINE", message },
    headers: {},
    config,
  };
  err.code = "OFFLINE";
  return Promise.reject(err);
};

const url = (config) => (config.url ?? "").split("?")[0].replace(/\/+$/, "");
const q = (config) => new URLSearchParams((config.url ?? "").split("?")[1] ?? "");
const paramsOf = (config) => ({
  ...Object.fromEntries(q(config).entries()),
  ...(config.params ?? {}),
});
const bodyOf = (config) => {
  try {
    return typeof config.data === "string" ? JSON.parse(config.data) : (config.data ?? {});
  } catch {
    return {};
  }
};
const userId = () => {
  try {
    return JSON.parse(localStorage.getItem("pharmakon_user") || "{}")?.id ?? null;
  } catch {
    return null;
  }
};

/** A failure with no HTTP response = connectivity loss (vs a real 4xx/5xx). */
const isNetworkError = (err) =>
  !err?.response &&
  (err?.code === "ERR_NETWORK" ||
    err?.code === "ECONNABORTED" ||
    err?.message === "Network Error" ||
    err?.message?.includes?.("timeout") ||
    !err?.code); // raw fetch/xhr rejection

const moduleActive = () => {
  const s = useOfflineStore.getState();
  return Boolean(s.serverEnabled && s.device && s.isOfflineCapable());
};

// ── Offline reads ─────────────────────────────────────────────────────────────
async function handleOfflineRead(config) {
  const path = url(config);
  const p = paramsOf(config);

  if (path === "/produits/search") {
    return ok(config, { success: true, data: await searchProduits(p.q ?? p.search ?? "", Number(p.limit ?? 15)), offline: true });
  }
  if (path === "/produits") {
    // Refonte WP-13 (AUD-SRCH-19) — the term filters offline too (shared matcher).
    const { rows, total } = await listProduits({ page: Number(p.page ?? 1), limit: Number(p.limit ?? 50), search: p.search ?? p.q ?? "" });
    return ok(config, { success: true, data: rows, total, page: Number(p.page ?? 1), offline: true });
  }
  const produitId = path.match(/^\/produits\/([0-9a-f-]{36})$/);
  if (produitId) {
    const prod = await getProduit(produitId[1]);
    return prod
      ? ok(config, { success: true, data: prod, offline: true })
      : offlineError(config, "Produit non disponible hors ligne");
  }
  // Cross-module XWP-25 — a scan at the counter resolves offline from the synced codes.
  if (path === "/codes/resolve") {
    return ok(config, { success: true, data: await resoudreCode(p.code ?? "", p.canal), offline: true });
  }
  if (path === "/stock/lots") {
    return ok(config, { success: true, data: await listLots(p.produit_id), offline: true });
  }
  if (path === "/clients") {
    const rows = await searchClients(p.search ?? p.q ?? "", Number(p.limit ?? 50));
    return ok(config, { success: true, data: rows, total: rows.length, offline: true });
  }
  const clientId = path.match(/^\/clients\/([0-9a-f-]{36})$/);
  if (clientId) {
    const c = await getClient(clientId[1]);
    return c ? ok(config, { success: true, data: c, offline: true }) : offlineError(config, "Client non disponible hors ligne");
  }
  if (path === "/medecins/dropdown" || path === "/medecins") {
    const rows = await listMedecins(p.search ?? p.q ?? "", Number(p.limit ?? 100));
    return ok(config, { success: true, data: rows, total: rows.length, offline: true });
  }
  if (path === "/caisse/current") {
    const cached = await metaGet("cache:/caisse/current");
    if (cached) return ok(config, { ...cached, offline: true });
    return offlineError(config, "Aucune session de caisse en cache — ouvrez-en une à la reconnexion");
  }
  if (path === "/sync/status") {
    return ok(config, { success: true, data: useOfflineStore.getState().config ?? { enabled: true }, offline: true });
  }
  if (path === "/auth/me") {
    // refreshUser() logs out on a failed /me — serve the cached user offline
    // so an offline reload keeps the session.
    let u = null;
    try { u = JSON.parse(localStorage.getItem("pharmakon_user") || "null"); } catch { /* ignore */ }
    if (u) return ok(config, { success: true, data: u, offline: true });
    return offlineError(config, "Session non disponible hors ligne");
  }
  // Notification pollers: keep the shell quiet, don't error the bell.
  if (path.startsWith("/notifications")) {
    return ok(config, { success: true, data: { count: 0, items: [], notifications: [] }, offline: true });
  }
  // Unknown GET → empty success. A page reads an empty list and renders its
  // empty state instead of a red error (the never-crash rule). Lists that
  // expect an envelope get total:0; detail reads get null data.
  return ok(config, { success: true, data: [], total: 0, offline: true, _unsupported: true });
}

// ── Offline writes ────────────────────────────────────────────────────────────
async function handleOfflineWrite(config) {
  const path = url(config);
  const body = bodyOf(config);

  // ── Sales: create draft (durable, IDB) → validate (enqueue ONE op) ──
  if (path === "/ventes/libre" || path === "/ventes/chifa") {
    const id = `OFF-${ulid()}`;
    const kind = path === "/ventes/chifa" ? "VENTE_CHIFA" : "VENTE_LIBRE";
    await idbPut("offline_drafts", { id, kind, body, created_at: new Date().toISOString() });
    const montant = (body.details ?? []).reduce((s, d) => s + (d.prix_unitaire_ppa ?? 0) * (d.quantite ?? 0), 0);
    return ok(config, {
      success: true,
      data: {
        id,
        statut: "EN_COURS",
        type_vente: kind === "VENTE_CHIFA" ? "CHIFA" : "LIBRE",
        numero_interne: null,
        montant_brut: montant,
        montant_total: montant,
        details: body.details ?? [],
        _offline: true,
      },
      offline: true,
    });
  }
  const validateMatch = path.match(/^\/ventes\/(OFF-[0-9A-Z]+)\/validate$/);
  if (validateMatch) {
    const draft = await idbGet("offline_drafts", validateMatch[1]);
    if (!draft) return offlineError(config, "Brouillon hors ligne introuvable — recommencez la vente");
    const { op, numero_provisoire } = await enqueue(
      draft.kind,
      { body: draft.body, validate: body ?? {} },
      { user_id: userId() },
    );
    await idbDelete("offline_drafts", validateMatch[1]);
    await applyLocalDeduction(draft.body.details ?? []);
    const montant = (draft.body.details ?? []).reduce((s, d) => s + (d.prix_unitaire_ppa ?? 0) * (d.quantite ?? 0), 0);
    return ok(config, {
      success: true,
      data: {
        id: op.id,
        statut: "VALIDEE",
        numero_interne: null,
        numero_provisoire,
        montant_brut: montant,
        montant_total: montant,
        details: draft.body.details ?? [],
        validated_at: op.offline_ts,
        _offline: true,
        _queued: true,
      },
      offline: true,
    });
  }

  // ── Stock correction (PIN-gated upstream; password NEVER stored) ──
  if (path === "/stock/corrections") {
    const { password, ...rest } = body;
    void password;
    const { op, numero_provisoire } = await enqueue(
      "STOCK_CORRECTION",
      { stock_lot_id: rest.stock_lot_id, nouvelle_quantite: rest.nouvelle_quantite ?? rest.qte_apres, motif: rest.motif },
      { user_id: userId() },
    );
    return ok(config, { success: true, data: { id: op.id, numero_provisoire, _queued: true }, offline: true });
  }

  // ── Client creation (temp id resolved in-batch at replay) ──
  if (path === "/clients") {
    const temp_id = `OFF-${ulid()}`;
    await enqueue("CLIENT_CREATE", { temp_id, body }, { user_id: userId() });
    return ok(config, { success: true, data: { id: temp_id, ...body, _offline: true, _queued: true }, offline: true });
  }

  // ── Caisse open (offline session start) → synthetic open + queued op ──
  if (path === "/caisse/open") {
    const temp_id = `OFF-${ulid()}`;
    const { op } = await enqueue("CAISSE_OPEN", { temp_id, fonds_ouverture: body.fonds_ouverture ?? 0 }, { user_id: userId() });
    const session = {
      success: true,
      data: { id: temp_id, statut: "OUVERTE", fonds_ouverture: body.fonds_ouverture ?? 0, opened_at: op.offline_ts, _offline: true },
    };
    await metaSet("cache:/caisse/current", session);
    return ok(config, { ...session, _queued: true, offline: true });
  }

  // ── Caisse close → provisional Z (plan §6.6) ──
  const closeMatch = path.match(/^\/caisse\/((?:OFF-)?[0-9A-Za-z-]+)\/close$/);
  if (closeMatch) {
    const { op } = await enqueue("CAISSE_CLOSE", { caisse_id: closeMatch[1], body }, { user_id: userId() });
    await metaSet("cache:/caisse/current", null);
    return ok(config, { success: true, data: { id: closeMatch[1], statut: "CLOTUREE", _queued: true, op_id: op.id }, offline: true });
  }

  return offlineError(config, "Cette opération nécessite la connexion — elle n'est pas disponible hors ligne");
}

async function serveOffline(config) {
  const method = (config.method ?? "get").toLowerCase();
  if (method === "get") return handleOfflineRead(config);
  if (["post", "put", "patch", "delete"].includes(method)) return handleOfflineWrite(config);
  return offlineError(config, "Cette opération nécessite la connexion");
}

// Reference GETs whose responses warm the local cache while online, so an
// outage degrades with fresh data. Keyed by path → meta cache key.
const PASSIVE_CACHE = { "/caisse/current": "cache:/caisse/current" };

/** Wraps the real network adapter as an offline-first circuit breaker. */
export function makeOfflineGate(networkAdapter) {
  return async function offlineGate(config) {
    // Internal sync traffic (deltas/batch/status) must always hit the network.
    if (config._bypassOfflineGate) return networkAdapter(config);
    if (!moduleActive()) return networkAdapter(config);

    // Already offline → serve local immediately, no network attempt.
    if (useOfflineStore.getState().status === "OFFLINE") return serveOffline(config);

    // Online → network first, with the circuit breaker on connectivity loss.
    try {
      const res = await networkAdapter(config);
      const key = PASSIVE_CACHE[url(config)];
      if (key && (config.method ?? "get").toLowerCase() === "get") {
        try {
          const parsed = typeof res.data === "string" ? JSON.parse(res.data) : res.data;
          await metaSet(key, parsed);
        } catch {
          /* cache warming is best-effort */
        }
      }
      return res;
    } catch (err) {
      if (isNetworkError(err)) {
        // Flip to OFFLINE now (instant, no waiting for the heartbeat) and
        // re-route this very request through the local database.
        const st = useOfflineStore.getState();
        if (st.status !== "OFFLINE") {
          st.setStatus("OFFLINE");
          try {
            window.dispatchEvent(
              new CustomEvent("pharmakon:toast", {
                detail: { message: "Connexion perdue — bascule en mode hors ligne", type: "warning" },
              }),
            );
          } catch {
            /* no-op */
          }
        }
        return serveOffline(config);
      }
      throw err; // a real HTTP error (4xx/5xx) — propagate unchanged
    }
  };
}
